(function (global) {
  const designIdPattern = /^dsg_[0-9a-f]{32}$/;

  const createPreviewLoadGate = () => {
    let generation = 0;
    let currentRequest = null;
    let readyVariantId = null;

    const begin = (variantId, imageUrl) => {
      generation += 1;
      currentRequest = Object.freeze({
        generation,
        variantId: String(variantId || ""),
        imageUrl: String(imageUrl || ""),
      });
      readyVariantId = null;
      return currentRequest;
    };

    const isCurrent = (request) => Boolean(
      request &&
      currentRequest &&
      request.generation === currentRequest.generation &&
      request.variantId === currentRequest.variantId &&
      request.imageUrl === currentRequest.imageUrl
    );

    const accept = (request) => {
      if (!isCurrent(request)) return false;
      readyVariantId = request.variantId;
      return true;
    };

    const isReadyFor = (variantId) => Boolean(
      currentRequest &&
      readyVariantId !== null &&
      readyVariantId === String(variantId || "") &&
      readyVariantId === currentRequest.variantId
    );

    return Object.freeze({accept, begin, isCurrent, isReadyFor});
  };

  const parseCartItemEdit = (search) => {
    const parameters = new URLSearchParams(search);
    const designId = parameters.get("design") || "";
    const lineKey = parameters.get("line") || "";
    if (
      parameters.get("mode") !== "cart-item" ||
      !designIdPattern.test(designId) ||
      !lineKey ||
      lineKey.length > 256
    ) {
      return null;
    }
    return {designId, lineKey};
  };

  const responseJson = async (response, message) => {
    if (!response || !response.ok) throw new Error(message);
    try {
      return await response.json();
    } catch (error) {
      throw new Error(message, {cause: error});
    }
  };

  const readablePropertiesFrom = (properties) => Object.fromEntries(
    Object.entries(properties || {}).filter(([key]) => !key.startsWith("_"))
  );

  const joinRoute = (path) => {
    const root = global.Shopify && global.Shopify.routes && global.Shopify.routes.root || "/";
    return `${String(root).replace(/\/?$/, "/")}${String(path).replace(/^\//, "")}`;
  };

  const loadCartItemEdit = async ({designId, lineKey}, dependencies = {}) => {
    if (!designIdPattern.test(designId) || !lineKey || lineKey.length > 256) {
      throw new Error("The cart item edit request is invalid.");
    }
    const request = dependencies.fetch || global.fetch;
    const [designResponse, cartResponse] = await Promise.all([
      request(`/apps/kova-designs/designs/${encodeURIComponent(designId)}`, {
        headers: {Accept: "application/json"},
      }),
      request(joinRoute("cart.js"), {
        cache: "no-store",
        headers: {Accept: "application/json"},
      }),
    ]);
    const [designBody, cart] = await Promise.all([
      responseJson(designResponse, "The saved design is unavailable."),
      responseJson(cartResponse, "The cart item is unavailable."),
    ]);
    const previewDesign = designBody && designBody.previewDesign;
    const responseDesignId = previewDesign && previewDesign.designId;
    const line = cart && Array.isArray(cart.items)
      ? cart.items.find((item) => item && item.key === lineKey)
      : null;
    if (
      responseDesignId !== designId ||
      !line ||
      !line.properties ||
      line.properties._design_id !== responseDesignId
    ) {
      throw new Error("The cart item is unavailable for editing.");
    }
    return {
      designId: responseDesignId,
      lineKey,
      quantity: line.quantity,
      originalVariantId: line.variant_id,
      readableProperties: readablePropertiesFrom(line.properties),
      previewDesign,
    };
  };

  const lockedStateFrom = (previewDesign) => {
    const engraving = previewDesign.engraving;
    return {
      text: engraving.sourceText,
      fontFamily: engraving.fontFamily,
      fontLabel: engraving.fontLabel,
      colorHex: engraving.colorHex,
      colorName: engraving.colorName,
      sizePercent: engraving.fontSizeMm / 35 * 100,
      xPercent: engraving.centerMmTop.x / 35 * 100,
      yPercent: engraving.centerMmTop.y / 45 * 100,
      rotationDeg: engraving.rotationDegClockwise,
    };
  };

  const replaceCartItem = async ({
    lineKey,
    expectedDesignId,
    draftInput,
    replacementLine,
  }, dependencies) => {
    const {saveDraft, addLine, changeLine, readCart} = dependencies;
    if (!designIdPattern.test(expectedDesignId) || typeof readCart !== "function") {
      throw new Error("The cart replacement request is invalid.");
    }

    const manualReviewError = (cause) => {
      const error = new Error(
        "We couldn't confirm the final cart state. Please review your cart before making any more changes.",
        {cause},
      );
      error.name = "CartManualReviewError";
      return error;
    };
    const concurrentChangeError = (cause, replacementRemoved = false) => {
      const error = new Error(
        replacementRemoved
          ? "Your cart changed in another tab. We removed the attempted replacement; please review your cart and retry."
          : "Your cart changed in another tab. Please review your cart and retry.",
        {cause},
      );
      error.name = "CartConcurrentChangeError";
      return error;
    };
    const replacementError = (cause, rollbackError) => {
      const error = new Error(
        "We couldn't replace the original cart item. Reconciliation proved your original cart item is unchanged. Please try again.",
        {cause},
      );
      error.name = "CartReplacementError";
      if (rollbackError) error.rollbackError = rollbackError;
      return error;
    };
    const cartItems = (cart) => {
      if (!cart || !Array.isArray(cart.items)) throw manualReviewError();
      return cart.items.filter(Boolean);
    };
    const lineDesignId = (line) => line && line.properties && line.properties._design_id;
    const originalFrom = (items) => items.find((line) => (
      line.key === lineKey && lineDesignId(line) === expectedDesignId
    ));
    const replacementsFrom = (items, draftDesignId) => items.filter(
      (line) => lineDesignId(line) === draftDesignId
    );
    const readBeforeMutation = async () => {
      try {
        return cartItems(await readCart());
      } catch (cause) {
        if (cause && cause.name === "CartManualReviewError") throw cause;
        const error = new Error(
          "We couldn't confirm your cart before updating. Your original cart item is unchanged. Please try again.",
          {cause},
        );
        error.name = "CartReadError";
        throw error;
      }
    };
    const readAfterMutation = async (cause) => {
      try {
        return cartItems(await readCart());
      } catch (readError) {
        throw manualReviewError(readError || cause);
      }
    };

    const draft = await saveDraft(draftInput);
    if (!draft || !designIdPattern.test(draft.designId) || draft.designId === expectedDesignId) {
      throw new Error("The replacement design receipt is invalid.");
    }

    const beforeItems = await readBeforeMutation();
    const beforeOriginal = originalFrom(beforeItems);
    if (!beforeOriginal) throw concurrentChangeError();
    const freshQuantity = Number(beforeOriginal.quantity);
    if (!Number.isInteger(freshQuantity) || freshQuantity < 1) throw concurrentChangeError();

    const requestedLine = {
      ...replacementLine,
      quantity: freshQuantity,
      properties: {
        ...replacementLine.properties,
        _design_id: draft.designId,
        _design_version: String(draft.designVersion),
        _design_checksum: draft.checksum,
      },
    };

    let addError;
    try {
      await addLine(requestedLine);
    } catch (error) {
      addError = error;
    }

    const afterAddItems = await readAfterMutation(addError);
    const afterAddOriginal = originalFrom(afterAddItems);
    const afterAddReplacements = replacementsFrom(afterAddItems, draft.designId);
    if (afterAddReplacements.length === 0) {
      if (
        afterAddOriginal &&
        Number(afterAddOriginal.quantity) === freshQuantity
      ) {
        const error = new Error(
          "We couldn't add the replacement line. Reconciliation proved your original cart item is unchanged. Please try again.",
          {cause: addError},
        );
        error.name = "CartAdditionError";
        throw error;
      }
      throw manualReviewError(addError);
    }
    if (afterAddReplacements.length !== 1) throw manualReviewError(addError);
    const replacementKey = afterAddReplacements[0].key;
    if (typeof replacementKey !== "string" || !replacementKey) throw manualReviewError(addError);

    const rollbackAndReconcile = async (cause, kind) => {
      let rollbackError;
      try {
        await changeLine(replacementKey, 0);
      } catch (error) {
        rollbackError = error;
      }
      const afterRollbackItems = await readAfterMutation(rollbackError || cause);
      const remainingReplacements = replacementsFrom(afterRollbackItems, draft.designId);
      const remainingOriginal = originalFrom(afterRollbackItems);
      if (remainingReplacements.length !== 0) throw manualReviewError(rollbackError || cause);
      if (kind === "concurrent") throw concurrentChangeError(cause, true);
      if (
        remainingOriginal &&
        Number(remainingOriginal.quantity) === freshQuantity
      ) {
        throw replacementError(cause, rollbackError);
      }
      throw manualReviewError(rollbackError || cause);
    };

    if (
      !afterAddOriginal ||
      Number(afterAddOriginal.quantity) !== freshQuantity ||
      Number(afterAddReplacements[0].quantity) !== freshQuantity
    ) {
      await rollbackAndReconcile(undefined, "concurrent");
    }

    try {
      await changeLine(lineKey, 0);
    } catch (error) {
      const afterRemoveItems = await readAfterMutation(error);
      const afterRemoveOriginal = originalFrom(afterRemoveItems);
      const afterRemoveReplacements = replacementsFrom(afterRemoveItems, draft.designId);
      if (afterRemoveReplacements.length === 1) {
        if (afterRemoveOriginal) await rollbackAndReconcile(error, "replacement");
        const originalIdentityStillPresent = afterRemoveItems.some((line) => (
          line.key === lineKey || lineDesignId(line) === expectedDesignId
        ));
        if (originalIdentityStillPresent) await rollbackAndReconcile(error, "concurrent");
        return;
      }
      if (
        afterRemoveOriginal &&
        afterRemoveReplacements.length === 0 &&
        Number(afterRemoveOriginal.quantity) === freshQuantity
      ) {
        throw replacementError(error);
      }
      throw manualReviewError(error);
    }
  };

  const api = Object.freeze({
    createPreviewLoadGate,
    loadCartItemEdit,
    lockedStateFrom,
    parseCartItemEdit,
    replaceCartItem,
  });
  if (typeof module !== "undefined" && module.exports) module.exports = api;
  global.KovaCartItemEdit = api;
})(globalThis);
